import { z } from 'zod';
import { decimalNoNegativo, decimalPositivo, paginacion, rif, uuid } from './comun.ts';
import type { RepartoFilaDTO } from './costos.ts';
import { METODOS_GASTO } from './gastos.ts';

export const ISLR_PCTS = ['0', '1', '2', '3', '5'] as const;
export const RETENCION_IVA_PCTS = ['0', '75', '100'] as const;

// RIF con el mismo formato canónico que clientes (X-12345678-9): ver comun.ts

export const proveedorInput = z.object({
  rif,
  razon_social: z.string().trim().min(2).max(160),
  direccion_fiscal: z.string().trim().max(300).default(''),
  telefono: z.string().trim().max(40).nullable().optional(),
  correo: z.email().max(160).nullable().optional(),
  tipo_contribuyente: z.enum(['ordinario', 'especial']).default('ordinario'),
  retencion_iva_pct: z.enum(RETENCION_IVA_PCTS).default('75'),
  islr_default_pct: z.enum(ISLR_PCTS).default('0'),
  /**
   * Persona natural (el ISLR lleva sustraendo, Decreto 1.808) o jurídica. Si no se indica sale del RIF: V, E y P son
   * naturales; J, G y C, jurídicas.
   */
  tipo_persona: z.enum(['natural', 'juridica']).optional(),
  activo: z.boolean().default(true),
  notas: z.string().trim().max(500).nullable().optional(),
});
export type ProveedorInput = z.infer<typeof proveedorInput>;
/** Edición PARCIAL: sin los `.default()` del alta (desactivar un proveedor le devolvía 75 % de retención, ordinario y sin dirección). */
export const proveedorEditarInput = proveedorInput.partial().extend({
  direccion_fiscal: z.string().trim().max(300).optional(),
  tipo_contribuyente: z.enum(['ordinario', 'especial']).optional(),
  retencion_iva_pct: z.enum(RETENCION_IVA_PCTS).optional(),
  islr_default_pct: z.enum(ISLR_PCTS).optional(),
  activo: z.boolean().optional(),
});
export type ProveedorEditarInput = z.infer<typeof proveedorEditarInput>;
export const proveedoresFiltros = paginacion.extend({ q: z.string().trim().max(80).optional(), activo: z.enum(['true', 'false']).optional() });

/**
 * Reparto de un gasto entre productos (spec 16, sección 4): la dueña escribe el monto en $ de cada producto; el sistema
 * no propone ni prorratea. `unidades` = entre cuántas se divide ese monto (nunca cero): en un servicio de la misma
 * compra son las de la compra; en una compra de servicio aparte o en Gastos, las de la compra elegida o la existencia.
 */
export const repartoFilaInput = z.object({
  producto_id: uuid,
  monto: decimalPositivo,
  unidades: decimalPositivo.optional(),
});
export type RepartoFilaInput = z.infer<typeof repartoFilaInput>;

/** Gasto SIN factura anotado en la pantalla de compra: al confirmarla se guarda en Gastos amarrado a ella (una vez). */
export const gastoDeCompraInput = z.object({
  concepto: z.string().trim().min(3, 'Describe el gasto').max(300),
  /** Monto en $. */
  monto: decimalPositivo,
  pagado_a: z.string().trim().min(2).max(120).nullable().optional(),
  metodo: z.enum(METODOS_GASTO),
  /** «No de la tienda» (gasto personal de la dueña). */
  personal: z.boolean().default(false),
  categoria_id: uuid.optional(),
  /** Entra al reparto entre los productos de esta compra: monto por producto (las unidades son las de la compra). */
  reparto: z.array(repartoFilaInput).min(1, 'Reparte el gasto entre los productos de la compra').max(300),
});
export type GastoDeCompraInput = z.infer<typeof gastoDeCompraInput>;

export const compraLineaInput = z
  .object({
    tipo: z.enum(['articulo', 'servicio', 'gasto']),
    producto_id: uuid.optional(),
    descripcion: z.string().trim().max(200).optional(),
    cantidad: decimalPositivo.default('1'),
    /** Costo unitario (artículo) o monto (servicio/gasto) en la moneda de la compra. */
    costo_unitario: decimalNoNegativo,
    gravado: z.boolean().default(true),
    aplica_islr: z.boolean().default(false),
    islr_pct: z.enum(ISLR_PCTS).optional(),
    /** Monto sujeto a ISLR (moneda de la compra); por defecto el subtotal de la línea. */
    islr_base: decimalNoNegativo.optional(),
    /** Código de concepto SENIAT de la retención (3 dígitos): va en el XML de la declaración. */
    islr_concepto: z.string().trim().regex(/^\d{3}$/, 'El código de concepto tiene 3 dígitos').optional(),
    /**
     * Servicio con factura que es costo de productos (spec 16): con artículos en la compra, «cargar al costo de los
     * artículos de esta compra»; en una compra solo de servicios, «¿Este servicio es costo de algún producto?» = sí.
     */
    cargar_al_costo: z.boolean().default(false),
    reparto: z.array(repartoFilaInput).max(300).default([]),
  })
  .refine((l) => l.tipo !== 'articulo' || !!l.producto_id, { message: 'La línea de artículo requiere producto', path: ['producto_id'] })
  .refine((l) => l.tipo === 'articulo' || (l.descripcion ?? '').length > 0, { message: 'Describe el servicio o gasto', path: ['descripcion'] })
  .refine((l) => !l.cargar_al_costo || l.tipo === 'servicio', { message: 'Solo un servicio se carga al costo', path: ['cargar_al_costo'] })
  .refine((l) => l.cargar_al_costo === l.reparto.length > 0, { message: 'Reparte el servicio entre los productos', path: ['reparto'] });
export type CompraLineaInput = z.infer<typeof compraLineaInput>;

/**
 * Tipo de documento del proveedor (spec 10, enmienda 2026-10-29): la factura de siempre o la nota de entrega sin factura.
 * La nota suma a la existencia y al costo real, pero no lleva IVA, ni retenciones, ni entra al libro de compras, al TXT
 * del IVA ni al XML del ISLR (para el ISLR es gasto no deducible).
 */
export const TIPOS_DOCUMENTO_COMPRA = ['factura', 'nota_entrega'] as const;
export type TipoDocumentoCompra = (typeof TIPOS_DOCUMENTO_COMPRA)[number];
export const TIPO_DOCUMENTO_COMPRA_ETIQUETA: Record<TipoDocumentoCompra, string> = { factura: 'Factura', nota_entrega: 'Nota de entrega' };
/** Condición de pago de la nota: contado (queda pagada al confirmarla) o crédito (cuenta por pagar). */
export const CONDICIONES_PAGO_NOTA = ['contado', 'credito'] as const;
export type CondicionPagoNota = (typeof CONDICIONES_PAGO_NOTA)[number];
export const METODOS_PAGO_COMPRA = ['efectivo_usd', 'efectivo_bs', 'zelle', 'transferencia', 'pago_movil'] as const;
/** El pago de una nota de contado: se registra al confirmarla, por todo el neto, como cualquier pago a proveedor. */
export const pagoContadoInput = z.object({
  metodo: z.enum(METODOS_PAGO_COMPRA),
  referencia: z.string().trim().max(60).optional(),
  observaciones: z.string().trim().max(300).optional(),
});
export type PagoContadoInput = z.infer<typeof pagoContadoInput>;

export const compraInput = z.object({
  proveedor_id: uuid,
  /** Factura (por defecto) o nota de entrega del proveedor sin factura. */
  tipo_documento: z.enum(TIPOS_DOCUMENTO_COMPRA).default('factura'),
  /** Solo la nota: contado o crédito (obligatorio en la nota; la factura no lo lleva). */
  condicion_pago: z.enum(CONDICIONES_PAGO_NOTA).optional(),
  /** Solo la nota de contado: método, referencia (obligatoria salvo en efectivo) y observaciones del pago. */
  pago_contado: pagoContadoInput.optional(),
  /** N° de la factura del proveedor o, en una nota, el N° de la nota (único por proveedor y tipo entre los no anulados). */
  numero_factura: z.string().trim().min(1).max(40),
  numero_control: z.string().trim().max(40).nullable().optional(),
  fecha_factura: z.iso.date(),
  /**
   * Fecha del período fiscal: con ella entra la compra al libro de compras. La pantalla la propone con el día del
   * registro. Nunca posterior a hoy, ni anterior a la fecha de la factura, ni anterior a la última ya confirmada en la
   * sucursal (409 `fecha_fiscal_anterior`). Sin este dato, la compra va al libro del mes de su factura.
   */
  fecha_fiscal: z.iso.date().optional(),
  almacen_id: uuid.nullable().optional(),
  moneda: z.enum(['USD', 'BS']).default('USD'),
  /** Tasa BCV de la factura (Bs por $); por defecto la vigente. */
  tasa_bcv: decimalPositivo.optional(),
  descuento_pct: decimalNoNegativo.optional(),
  lineas: z.array(compraLineaInput).min(1, 'Agrega al menos una línea').max(300),
  observaciones: z.string().trim().max(500).optional(),
  /** «Gastos sin factura de esta compra» (spec 16, 2.4): separados de la factura; al confirmar pasan a Gastos. */
  gastos_sin_factura: z.array(gastoDeCompraInput).max(20).default([]),
  /** La orden de compra de la que viene (spec 21): al crear la compra, la orden queda «Pasada a compra». */
  orden_compra_id: uuid.optional(),
});
export type CompraInput = z.infer<typeof compraInput>;
export const compraEditarInput = compraInput.extend({ motivo: z.string().trim().max(300).optional() });
export type CompraEditarInput = z.infer<typeof compraEditarInput>;
export const anularCompraInput = z.object({ motivo: z.string().trim().min(3).max(300) });
export const compraPagoInput = z.object({
  metodo: z.enum(METODOS_PAGO_COMPRA),
  /** Monto en $ a abonar a la CxP. */
  monto_usd: decimalPositivo,
  referencia: z.string().trim().max(60).optional(),
  observaciones: z.string().trim().max(300).optional(),
});
export type CompraPagoInput = z.infer<typeof compraPagoInput>;
export const comprasFiltros = paginacion.extend({
  estado: z.enum(['borrador', 'confirmada', 'anulada']).optional(),
  proveedor_id: uuid.optional(),
  periodo: z.string().regex(/^\d{6}$/).optional(),
  q: z.string().trim().max(80).optional(),
  con_saldo: z.enum(['true', 'false']).optional(),
  /** Solo facturas o solo notas de entrega (sin factura). */
  tipo_documento: z.enum(TIPOS_DOCUMENTO_COMPRA).optional(),
});
export type ComprasFiltros = z.infer<typeof comprasFiltros>;
export const periodoQuery = z.object({ periodo: z.string().regex(/^\d{6}$/, 'Período AAAAMM') });
/** TXT de retenciones de IVA: se declara por quincena (1 = del 1 al 15, 2 = del 16 al último día). */
export const txtIvaQuery = periodoQuery.extend({ quincena: z.enum(['1', '2']) });
/**
 * Numerar comprobantes provisionales: pasan a definitivos con el siguiente número del período indicado (por defecto
 * el mes en curso). Sin `comprobante_ids` se numeran todos los provisionales de la compra.
 */
export const numerarComprobantesInput = z.object({
  periodo: z.string().regex(/^\d{6}$/, 'Período AAAAMM').optional(),
  comprobante_ids: z.array(uuid).max(20).optional(),
});
export type NumerarComprobantesInput = z.infer<typeof numerarComprobantesInput>;

/** Concepto de retención de ISLR (Decreto 1.808) con su código SENIAT. */
export interface IslrConceptoDTO {
  codigo: string;
  descripcion: string;
  tipo_persona: 'natural' | 'juridica';
  pct: string;
  con_sustraendo: boolean;
}

export interface ProveedorDTO {
  id: string;
  rif: string;
  razon_social: string;
  direccion_fiscal: string;
  telefono: string | null;
  correo: string | null;
  tipo_contribuyente: 'ordinario' | 'especial';
  retencion_iva_pct: string;
  islr_default_pct: string;
  tipo_persona: 'natural' | 'juridica';
  activo: boolean;
  notas: string | null;
  cxp_saldo: string;
  compras: number;
  creado_en: string;
}

export interface CompraLineaDTO {
  id: string;
  orden: number;
  tipo: 'articulo' | 'servicio' | 'gasto';
  producto_id: string | null;
  producto_codigo: string | null;
  descripcion: string;
  cantidad: string;
  costo_unitario: string;
  subtotal: string;
  gravado: boolean;
  iva: string;
  aplica_islr: boolean;
  islr_pct: string;
  islr_base: string;
  islr_monto: string;
  islr_concepto: string | null;
  /** Sustraendo del grupo (persona natural), en $; va en la primera línea del grupo. */
  islr_sustraendo: string;
  /** Bs fiscales de la línea: subtotal, base sujeta a ISLR y sustraendo (el de la factura si vino en Bs). */
  subtotal_bs: string;
  islr_base_bs: string;
  islr_sustraendo_bs: string;
  /** Siempre 0: el costo de los gastos va por `costos_asignados` (spec 16), no por prorrateo. */
  gasto_prorrateado: string;
  /** Costo de factura por unidad (artículo). */
  costo_real_unitario: string;
  /**
   * Costo por unidad tal como se escribió en la factura, en su moneda y SIN el «Descuento %» de la compra: el que la
   * pantalla vuelve a mostrar al corregir (`costo_unitario` ya lleva el descuento).
   */
  costo_escrito: string;
  /** Base de ISLR escrita a mano, en la moneda de la factura; null cuando la base es todo el renglón. */
  islr_base_escrita: string | null;
  /** Servicio cargado al costo de productos, con su reparto. */
  cargar_al_costo: boolean;
  reparto: RepartoFilaDTO[];
}

/** Gasto sin factura de la compra: anotado en el borrador, o ya guardado en Gastos (con su número) al confirmar. */
export interface GastoDeCompraDTO {
  id: string | null;
  numero: number | null;
  concepto: string;
  monto: string;
  pagado_a: string | null;
  metodo: string;
  personal: boolean;
  categoria_id: string | null;
  estado: 'borrador' | 'pendiente' | 'aprobado' | 'rechazado' | 'anulado';
  reparto: RepartoFilaDTO[];
}

/** Costo resultante por artículo de la compra: factura + gastos asignados (con y sin factura) por unidad. */
export interface CostoResultanteDTO {
  producto_id: string;
  codigo: string;
  nombre: string;
  cantidad: string;
  costo_factura: string;
  gastos_con_factura: string;
  gastos_sin_factura: string;
  costo_real: string;
}

export interface ComprobanteDTO {
  id: string;
  tipo: 'iva' | 'islr';
  pct: string;
  /** null = PROVISIONAL: se entregó al proveedor sin número y se numera después. */
  numero: string | null;
  provisional: boolean;
  numerado_en: string | null;
  numerado_por_nombre: string | null;
  version: number;
  estado: 'vigente' | 'corregido' | 'anulado';
  periodo: string;
  base: string;
  /** Lo retenido (ISLR: ya con el sustraendo descontado). */
  monto: string;
  /** Sustraendo aplicado (ISLR a persona natural), en $ y en Bs. */
  sustraendo: string;
  sustraendo_bs: string;
  base_bs: string;
  monto_bs: string;
  /** `concepto_nombre`: el nombre del concepto de ISLR en el catálogo (lo que imprime el comprobante). */
  detalle: { descripcion: string; base: string; pct: string; monto: string; concepto?: string | null; concepto_nombre?: string | null; base_bs?: string; monto_bs?: string }[];
  generado_por_nombre: string;
  generado_en: string;
  impresiones: number;
}

export interface CompraPagoDTO {
  id: string;
  metodo: string;
  moneda: 'USD' | 'BS';
  monto: string;
  monto_usd: string;
  /** Tasa BCV con que se llevó a Bs un pago en Bs (null si fue en $): la pantalla lo muestra en su moneda (4-H7). */
  tasa_aplicada: string | null;
  referencia: string | null;
  usuario_nombre: string;
  pagado_en: string;
}

export interface CompraResumenDTO {
  id: string;
  numero_formateado: string | null;
  estado: 'borrador' | 'confirmada' | 'anulada';
  /** Factura o nota de entrega sin factura (la pantalla la marca «Sin factura»). */
  tipo_documento: TipoDocumentoCompra;
  /** Solo la nota: contado o crédito; null en la factura. */
  condicion_pago: CondicionPagoNota | null;
  proveedor_id: string;
  proveedor_razon_social: string;
  proveedor_rif: string;
  numero_factura: string;
  numero_control: string | null;
  fecha_factura: string;
  /** Fecha del período fiscal; null = la de la factura. */
  fecha_fiscal: string | null;
  periodo: string;
  moneda: 'USD' | 'BS';
  total_factura: string;
  retencion_iva: string;
  retencion_islr: string;
  neto: string;
  cxp_saldo: string;
  requiere_txt: boolean;
  /** Corregida, anulada o numerada después de bajar el XML del ISLR de su período: hay que bajarlo de nuevo. */
  requiere_xml: boolean;
  /** Comprobantes de retención vigentes que siguen sin número (provisionales). */
  comprobantes_provisionales: number;
  version: number;
  lineas: number;
  creado_por_nombre: string;
  creado_en: string;
}

export interface CompraDTO extends Omit<CompraResumenDTO, 'lineas'> {
  sucursal_id: string;
  sucursal_nombre: string;
  proveedor_direccion_fiscal: string;
  almacen_id: string | null;
  almacen_nombre: string | null;
  tasa_bcv: string;
  descuento_pct: string;
  bruto: string;
  descuento: string;
  base_gravada: string;
  base_exenta: string;
  iva_pct: string;
  iva: string;
  gastos_anexos: string;
  retencion_iva_pct: string;
  cxp_pagado: string;
  /** Importes FISCALES en Bs, guardados con la compra (los de la factura si vino en Bs): son los del libro, el comprobante, el TXT y el XML. */
  total_factura_bs: string;
  base_gravada_bs: string;
  base_exenta_bs: string;
  iva_bs: string;
  retencion_iva_bs: string;
  retencion_islr_bs: string;
  confirmada_por_nombre: string | null;
  confirmada_en: string | null;
  txt_generado_en: string | null;
  xml_generado_en: string | null;
  anulada_por_nombre: string | null;
  anulada_en: string | null;
  motivo_anulacion: string | null;
  observaciones: string | null;
  lineas: CompraLineaDTO[];
  comprobantes: ComprobanteDTO[];
  pagos: CompraPagoDTO[];
  versiones: { version: number; motivo: string; usuario_nombre: string; creado_en: string }[];
  /** La orden de compra de la que vino (spec 21), si vino de una. */
  orden_compra?: { id: string; numero_formateado: string } | null;
  /** Nota de contado: el pago que se registra (borrador) o se registró (confirmada) al confirmarla. */
  pago_contado: PagoContadoInput | null;
  /** Cálculo preliminar (borrador): lo que generará confirmar. `monto` ya descuenta el sustraendo. */
  islr_grupos: { pct: string; base: string; monto: string; sustraendo: string; base_bs: string; monto_bs: string; sustraendo_bs: string }[];
  proveedor_tipo_persona: 'natural' | 'juridica';
  /** Gastos sin factura de esta compra (borrador: anotados; confirmada: los del módulo Gastos amarrados a ella). */
  gastos_sin_factura: GastoDeCompraDTO[];
  /** Costo resultante de cada artículo con los gastos de esta compra. */
  costos: CostoResultanteDTO[];
}

export interface EmpresaAgenteDTO {
  razon_social: string;
  rif: string;
  direccion_fiscal: string;
  telefono: string | null;
  /** Firma del agente de retención para los comprobantes (data URL PNG o JPEG); null = se firma a mano. */
  firma: string | null;
}
